import React, { useState, useCallback } from 'react';
import {
  Pane,
  Heading,
  TextInputField,
  SelectField,
  TextareaField,
  Button,
  FileUploader,
  FileCard,
  toaster,
  Alert,
} from 'evergreen-ui';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import 'pdfjs-dist/build/pdf.worker.mjs';
import './AdminProducts.css';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

const AdminProducts = () => {
  const [productForm, setProductForm] = useState({
    product_title: '',
    price: '',
    image1: null,
    image2: null,
    rating: 0,
    reviews: 0,
    stock: 0,
    isAvailable: 'Yes',
    sku: '',
    description: '',
    color: '',
    size: '',
    material: '',
    brand: '',
    weight: '',
    dimensions: '',
  });
  const [docFile, setDocFile] = useState(null);
  const [docRejections, setDocRejections] = useState([]);
  const [image1Rejections, setImage1Rejections] = useState([]);
  const [image2Rejections, setImage2Rejections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:3000';

  // Handle input changes
  const handleProductChange = (e) => {
    const { name, value, type, files } = e.target;
    setProductForm((prev) => ({
      ...prev,
      [name]: type === 'file' ? files[0] : 
              type === 'number' ? parseFloat(value) || 0 : 
              name === 'isAvailable' ? value : 
              value,
    }));
  };

  // Download Product Template
  const handleDownloadTemplate = async () => {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Product Template', bold: true, size: 28 })],
            }),
            new Paragraph(' '),
            new Paragraph({
              children: [
                new TextRun({ text: 'Title: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Price (USD): ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Rating (0-5): ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Reviews: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Stock: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Available: ', bold: true }),
                new TextRun('Yes / No'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'SKU: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Description: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Color: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Size: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Material: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Brand: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Weight: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Dimensions: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Product_Template.docx';
    link.click();
  };

  // Handle document upload
  const handleDocUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;

    setDocFile(file);
    setDocRejections([]);
    setExtracting(true);

    try {
      let rawText = '';
      if (file.type === 'application/pdf') {
        rawText = await extractTextFromPDF(file);
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        rawText = await extractTextFromWord(file);
      } else {
        toaster.danger('Please upload a Word (.docx) or PDF file.');
        setDocFile(null);
        return;
      }

      fillFieldsFromExtractedText(rawText);
      toaster.success('Fields auto-filled from the uploaded document!');
    } catch (err) {
      console.error('Extraction error:', err);
      toaster.danger('Failed to read the document.');
    } finally {
      setExtracting(false);
    }
  };

// ----------------------------------------------------------------------
// FIXED PDF TEXT EXTRACTION
// ----------------------------------------------------------------------
const extractTextFromPDF = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let allText = '';

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    // Join fragments but avoid merging adjacent numeric pieces or units
    const pageText = textContent.items
      .map((item) => item.str)
      .join(' ')
      .replace(/\s{2,}/g, ' ')
      // Remove stray non-printable characters
      .replace(/[^\x20-\x7E]/g, '')
      // Fix broken numeric tokens like "59 . 99" → "59.99"
      .replace(/(\d)\s*\.\s*(\d)/g, '$1.$2')
      // Remove duplicated trailing digits like 59.99054 → 59.99
      .replace(/(\d+\.\d{2})\d+/g, '$1');

    allText += pageText + '\n';
  }

  // Insert newlines before known labels to separate merged text
  let normalized = allText
    .replace(/\s*(Title\s*:)/gi, '\n$1 ')
    .replace(/\s*(Price\s*\(USD\)\s*:)/gi, '\n$1 ')
    .replace(/\s*(Rating\s*\(?0\s*-\s*5\)?\s*:)/gi, '\n$1 ')
    .replace(/\s*(Reviews\s*:)/gi, '\n$1 ')
    .replace(/\s*(Stock\s*:)/gi, '\n$1 ')
    .replace(/\s*(Available\s*:)/gi, '\n$1 ')
    .replace(/\s*(SKU\s*:)/gi, '\n$1 ')
    .replace(/\s*(Description\s*:)/gi, '\n$1 ')
    .replace(/\s*(Color\s*:)/gi, '\n$1 ')
    .replace(/\s*(Size\s*:)/gi, '\n$1 ')
    .replace(/\s*(Material\s*:)/gi, '\n$1 ')
    .replace(/\s*(Brand\s*:)/gi, '\n$1 ')
    .replace(/\s*(Weight\s*:)/gi, '\n$1 ')
    .replace(/\s*(Dimensions\s*:)/gi, '\n$1 ')
    .replace(/\r/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

  return normalized;
};

// ----------------------------------------------------------------------
// Improved Word text extraction
// ----------------------------------------------------------------------
const extractTextFromWord = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  let text = result.value
    .replace(/\r/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();

  // Force newlines before known labels to separate joined lines
  text = text
    .replace(/\s*(Title\s*:)/gi, '\n$1 ')
    .replace(/\s*(Price\s*\(USD\)\s*:)/gi, '\n$1 ')
    .replace(/\s*(Rating\s*\(0-5\)\s*:)/gi, '\n$1 ')
    .replace(/\s*(Reviews\s*:)/gi, '\n$1 ')
    .replace(/\s*(Stock\s*:)/gi, '\n$1 ')
    .replace(/\s*(Available\s*:)/gi, '\n$1 ')
    .replace(/\s*(SKU\s*:)/gi, '\n$1 ')
    .replace(/\s*(Description\s*:)/gi, '\n$1 ')
    .replace(/\s*(Color\s*:)/gi, '\n$1 ')
    .replace(/\s*(Size\s*:)/gi, '\n$1 ')
    .replace(/\s*(Material\s*:)/gi, '\n$1 ')
    .replace(/\s*(Brand\s*:)/gi, '\n$1 ')
    .replace(/\s*(Weight\s*:)/gi, '\n$1 ')
    .replace(/\s*(Dimensions\s*:)/gi, '\n$1 ')
    .trim();

  return text;
};

// ----------------------------------------------------------------------
// FIXED FIELD EXTRACTION / PARSING
// ----------------------------------------------------------------------
const fillFieldsFromExtractedText = (rawText) => {
  if (!rawText) {
    toaster.warning('No text found in the document.');
    return;
  }

  const text = rawText.replace(/\r/g, '\n').trim();

  // Flexible label regex (handles (USD), (0 - 5), etc.)
  const labelRegex = /(?:^|\n)\s*(Title|Price\s*\(USD\)|Rating\s*\(?0\s*-\s*5\)?|Reviews|Stock|Available|SKU|Description|Color|Size|Material|Brand|Weight|Dimensions)\s*[:：-]?\s*([^\n]*)/gi;

  const fields = {
    product_title: '',
    price: '',
    rating: 0,
    reviews: 0,
    stock: 0,
    isAvailable: 'Yes',
    sku: '',
    description: '',
    color: '',
    size: '',
    material: '',
    brand: '',
    weight: '',
    dimensions: '',
  };

  for (const match of text.matchAll(labelRegex)) {
    let [, label, value] = match;
    label = label.toLowerCase().trim();
    value = value.trim();

    if (label.includes('title')) fields.product_title = value;
    else if (label.includes('price')) {
      // Ensure clean numeric extraction
      const priceNum = value.match(/\d+(\.\d+)?/);
      fields.price = priceNum ? parseFloat(priceNum[0]) : 0;
    } else if (label.includes('rating')) {
      const ratingNum = value.match(/\d+(\.\d+)?/);
      fields.rating = ratingNum ? parseFloat(ratingNum[0]) : 0;
    } else if (label.includes('reviews')) {
      fields.reviews = parseInt(value.replace(/\D/g, '')) || 0;
    } else if (label.includes('stock')) {
      fields.stock = parseInt(value.replace(/\D/g, '')) || 0;
    } else if (label.includes('available')) {
      fields.isAvailable = value.toLowerCase().includes('yes') ? 'Yes' : 'No';
    } else if (label.includes('sku')) fields.sku = value;
    else if (label.includes('description')) fields.description = value;
    else if (label.includes('color')) fields.color = value;
    else if (label.includes('size')) fields.size = value;
    else if (label.includes('material')) fields.material = value;
    else if (label.includes('brand')) fields.brand = value;
    else if (label.includes('weight')) fields.weight = value;
    else if (label.includes('dimensions')) fields.dimensions = value;
  }

  setProductForm((prev) => ({ ...prev, ...fields }));

  const missing = Object.keys(fields).filter(
    (key) => !fields[key] && key !== 'image1' && key !== 'image2'
  );
  if (missing.length) {
    toaster.warning(
      `Could not extract: ${missing.join(', ')}. Please ensure your PDF text matches the template format.`
    );
  }
};

  // Image upload handlers
  const handleImage1Change = useCallback((files) => {
    if (files.length) {
      setProductForm((prev) => ({ ...prev, image1: files[0] }));
      setImage1Rejections([]);
    }
  }, []);

  const handleImage1Rejected = useCallback((rejections) => {
    setImage1Rejections(rejections);
    toaster.danger('Invalid image 1. Max 10 MB, one file only.');
  }, []);

  const handleImage1Remove = useCallback(() => {
    setProductForm((prev) => ({ ...prev, image1: null }));
    setImage1Rejections([]);
  }, []);

  const handleImage2Change = useCallback((files) => {
    if (files.length) {
      setProductForm((prev) => ({ ...prev, image2: files[0] }));
      setImage2Rejections([]);
    }
  }, []);

  const handleImage2Rejected = useCallback((rejections) => {
    setImage2Rejections(rejections);
    toaster.danger('Invalid image 2. Max 10 MB, one file only.');
  }, []);

  const handleImage2Remove = useCallback(() => {
    setProductForm((prev) => ({ ...prev, image2: null }));
    setImage2Rejections([]);
  }, []);

  // Submit form
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const requiredFields = ['product_title', 'price', 'stock', 'sku', 'image1', 'image2'];
    const missingFields = requiredFields.filter((key) => !productForm[key]);
    if (missingFields.length) {
      toaster.danger(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    const formData = new FormData();
    Object.keys(productForm).forEach((key) => {
      if (productForm[key] !== null && productForm[key] !== '') {
        formData.append(key, productForm[key]);
      }
    });

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/products`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to add product');

      setSuccess('Product added successfully!');
      setProductForm({
        product_title: '',
        price: '',
        image1: null,
        image2: null,
        rating: 0,
        reviews: 0,
        stock: 0,
        isAvailable: 'Yes',
        sku: '',
        description: '',
        color: '',
        size: '',
        material: '',
        brand: '',
        weight: '',
        dimensions: '',
      });
      setDocFile(null);
      setDocRejections([]);
      setImage1Rejections([]);
      setImage2Rejections([]);
    } catch (err) {
      setError(err.message);
      toaster.danger(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Pane className="admin-products-container">
      <Heading size={800} className="products-title">
        Add New Product
      </Heading>

      {error && (
        <Alert intent="danger" title={error} marginBottom={16} />
      )}
      {success && (
        <Alert intent="success" title={success} marginBottom={16} />
      )}

      <form className="admin-products-form" onSubmit={handleProductSubmit}>
        <Pane className="columns-container">
          {/* Left Column: Manual Inputs */}
          <Pane className="left-column">
            <Pane className="form-row">
              <span className="form-label">Title</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="product_title"
                  value={productForm.product_title}
                  onChange={handleProductChange}
                  required
                  aria-required="true"
                  aria-label="Product title input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Price (USD)</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="price"
                  type="number"
                  value={productForm.price}
                  onChange={handleProductChange}
                  step="0.01"
                  required
                  aria-required="true"
                  aria-label="Product price input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Image 1</span>
              <Pane className="form-input">
                <FileUploader
                  label=""
                  description="Max 10 MB, one image"
                  maxSizeInBytes={10 * 1024 ** 2}
                  maxFiles={1}
                  onChange={handleImage1Change}
                  onRejected={handleImage1Rejected}
                  renderFile={(file) => (
                    <FileCard
                      key={file.name}
                      name={file.name}
                      sizeInBytes={file.size}
                      type={file.type}
                      onRemove={handleImage1Remove}
                      isInvalid={image1Rejections.some((r) => r.file === file)}
                      validationMessage={
                        image1Rejections.find((r) => r.file === file)?.errors?.map((e) => e.message).join(', ')
                      }
                    />
                  )}
                  values={productForm.image1 ? [productForm.image1] : []}
                />
                {image1Rejections.length > 0 && (
                  <ul className="file-rejections">
                    {image1Rejections.map(({ file, errors }) => (
                      <li key={file.name} className="rejection-item">
                        ❌ <strong>{file.name}</strong> — {errors.map((e) => e.message).join(', ')}
                      </li>
                    ))}
                  </ul>
                )}
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Image 2</span>
              <Pane className="form-input">
                <FileUploader
                  label=""
                  description="Max 10 MB, one image"
                  maxSizeInBytes={10 * 1024 ** 2}
                  maxFiles={1}
                  onChange={handleImage2Change}
                  onRejected={handleImage2Rejected}
                  renderFile={(file) => (
                    <FileCard
                      key={file.name}
                      name={file.name}
                      sizeInBytes={file.size}
                      type={file.type}
                      onRemove={handleImage2Remove}
                      isInvalid={image2Rejections.some((r) => r.file === file)}
                      validationMessage={
                        image2Rejections.find((r) => r.file === file)?.errors?.map((e) => e.message).join(', ')
                      }
                    />
                  )}
                  values={productForm.image2 ? [productForm.image2] : []}
                />
                {image2Rejections.length > 0 && (
                  <ul className="file-rejections">
                    {image2Rejections.map(({ file, errors }) => (
                      <li key={file.name} className="rejection-item">
                        ❌ <strong>{file.name}</strong> — {errors.map((e) => e.message).join(', ')}
                      </li>
                    ))}
                  </ul>
                )}
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Rating (0-5)</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="rating"
                  type="number"
                  value={productForm.rating}
                  onChange={handleProductChange}
                  min={0}
                  max={5}
                  step="0.1"
                  aria-label="Product rating input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Reviews</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="reviews"
                  type="number"
                  value={productForm.reviews}
                  onChange={handleProductChange}
                  min={0}
                  aria-label="Product reviews input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Stock</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="stock"
                  type="number"
                  value={productForm.stock}
                  onChange={handleProductChange}
                  min={0}
                  required
                  aria-required="true"
                  aria-label="Product stock input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Available</span>
              <Pane className="form-input">
                <SelectField
                  label=""
                  name="isAvailable"
                  value={productForm.isAvailable}
                  onChange={handleProductChange}
                  aria-label="Product availability select"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </SelectField>
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">SKU</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="sku"
                  value={productForm.sku}
                  onChange={handleProductChange}
                  required
                  aria-required="true"
                  aria-label="Product SKU input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Description</span>
              <Pane className="form-input">
                <TextareaField
                  label=""
                  name="description"
                  value={productForm.description}
                  onChange={handleProductChange}
                  minHeight={100}
                  aria-label="Product description input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Color</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="color"
                  value={productForm.color}
                  onChange={handleProductChange}
                  placeholder="e.g., Red, Blue"
                  aria-label="Product color input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Size</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="size"
                  value={productForm.size}
                  onChange={handleProductChange}
                  placeholder="e.g., S, M, L"
                  aria-label="Product size input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Material</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="material"
                  value={productForm.material}
                  onChange={handleProductChange}
                  placeholder="e.g., Cotton, Leather"
                  aria-label="Product material input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Brand</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="brand"
                  value={productForm.brand}
                  onChange={handleProductChange}
                  placeholder="e.g., Nike, Adidas"
                  aria-label="Product brand input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Weight</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="weight"
                  value={productForm.weight}
                  onChange={handleProductChange}
                  placeholder="e.g., 1.2kg"
                  aria-label="Product weight input"
                />
              </Pane>
            </Pane>
            <Pane className="form-row">
              <span className="form-label">Dimensions</span>
              <Pane className="form-input">
                <TextInputField
                  label=""
                  name="dimensions"
                  value={productForm.dimensions}
                  onChange={handleProductChange}
                  placeholder="e.g., 10x20x5 cm"
                  aria-label="Product dimensions input"
                />
              </Pane>
            </Pane>
          </Pane>

          {/* Divider */}
          <Pane className="divider" />

          {/* Right Column: Template Upload/Download */}
          <Pane className="right-column">
            <Pane className="file-section">
              <Heading size={500}>Auto-fill from Template</Heading>
              <p className="template-desc">
                Download the Word template, fill it, then upload it here.
              </p>
              <Button
                appearance="minimal"
                intent="success"
                onClick={handleDownloadTemplate}
              >
                ⬇️ Download Template
              </Button>
              <hr className="template-divider" />
              <FileUploader
                label=""
                description="Word (.docx) or PDF, max 10 MB"
                maxSizeInBytes={10 * 1024 ** 2}
                maxFiles={1}
                onChange={handleDocUpload}
                onRejected={setDocRejections}
                renderFile={(file) => (
                  <FileCard
                    key={file.name}
                    name={file.name}
                    sizeInBytes={file.size}
                    type={file.type}
                    onRemove={() => setDocFile(null)}
                    isInvalid={docRejections.some((r) => r.file === file)}
                    validationMessage={
                      docRejections.find((r) => r.file === file)?.errors?.map((e) => e.message).join(', ')
                    }
                  />
                )}
                values={docFile ? [docFile] : []}
              />
              {docRejections.length > 0 && (
                <ul className="file-rejections">
                  {docRejections.map(({ file, errors }) => (
                    <li key={file.name} className="rejection-item">
                      ❌ <strong>{file.name}</strong> — {errors.map((e) => e.message).join(', ')}
                    </li>
                  ))}
                </ul>
              )}
              {extracting && <p className="loading-text">Extracting text…</p>}
            </Pane>
          </Pane>
        </Pane>

        <Button
          appearance="primary"
          intent="success"
          height={44}
          iconBefore="add"
          type="submit"
          isLoading={loading}
        >
          {loading ? 'Adding Product…' : 'Add Product'}
        </Button>
      </form>
    </Pane>
  );
};

export default AdminProducts;